import {
	FetchSurveyLanuageResponseObject,
	FetchAssistanceOptionReasonResponse,
	FetchSurveyAssignmentAnswersResponse,
} from '@/features/patientScreening';
import { baseApi } from '@/lib/rtkQuery/baseApi';

import {
	PatientSurveyRequest,
	PatientDobRequest,
	UpdateSurveyAnswersRequest,
	SendContactRequest,
	PreferredLangaugeRequestObject,
	SendConsentConfirmationEmailRequest,
	UpdateContactInfoRequest,
} from '../types';
import { constructQueryString } from '../utils';

export const patientSurveyApi = baseApi
	.enhanceEndpoints({
		addTagTypes: ['SurveyAnswers'],
	})
	.injectEndpoints({
		endpoints: (build) => ({
			fetchSurvey: build.query<FetchSurveyAssignmentAnswersResponse, PatientSurveyRequest>({
				query: ({ token, medium }) => {
					const queryString = medium ? constructQueryString({ m: medium }) : '';
					return {
						url: `v1/survey/token/${token}/${queryString}`,
						method: 'GET',
					};
				},
				providesTags: ['SurveyAnswers'],
			}),
			checkPatientDob: build.mutation<null, PatientDobRequest>({
				query: ({ token, dob }) => {
					return {
						url: `v1/survey/token/${token}/dob/`,
						method: 'post',
						body: { dob },
					};
				},
			}),
			setPreferredSurveyLanguage: build.mutation<null, PreferredLangaugeRequestObject>({
				query: ({ token, preferred_language }) => {
					return {
						url: `v1/survey/token/${token}/set-preferred-language`,
						method: 'post',
						body: { preferred_language },
					};
				},
				invalidatesTags: ['SurveyAnswers'],
			}),
			updateSurveyAnswers: build.mutation<
				FetchSurveyAssignmentAnswersResponse,
				UpdateSurveyAnswersRequest
			>({
				query: ({ token, surveyData, medium }) => {
					const queryString = medium ? constructQueryString({ m: medium }) : '';
					return {
						url: `v1/survey/token/${token}/${queryString}`,
						method: 'PATCH',
						body: surveyData,
					};
				},
				invalidatesTags: ['SurveyAnswers'],
			}),
			fetchAssistanceOptions: build.query<FetchAssistanceOptionReasonResponse[], void>({
				query: () => {
					return {
						url: `v1/survey/options/assistance-reasons/`,
						method: 'GET',
					};
				},
			}),
			sendContactRequest: build.mutation<null, SendContactRequest>({
				query: ({ token, preferred_contact_number, reason }) => {
					return {
						url: `v1/survey/token/${token}/assistance/`,
						method: 'post',
						body: { preferred_contact_number, reason },
					};
				},
			}),
			fetchSurveyLanguage: build.query<
				FetchSurveyLanuageResponseObject,
				PatientSurveyRequest
			>({
				query: ({ token }) => {
					return {
						url: `v1/survey/token/${token}/language/`,
						method: 'GET',
					};
				},
			}),
			sendConsentConfirmationEmail: build.mutation<null, SendConsentConfirmationEmailRequest>(
				{
					query: ({ token, email }) => {
						return {
							url: `v1/survey/token/${token}/consent-email/`,
							method: 'post',
							body: { email },
						};
					},
				}
			),
			updateContactInfo: build.mutation<null, UpdateContactInfoRequest>({
				query: ({ token, email, mobile }) => {
					return {
						url: `v1/survey/token/${token}/update-contact/`,
						method: 'POST',
						body: { email, mobile },
					};
				},
			}),
		}),
	});

export const {
	useFetchSurveyQuery,
	useCheckPatientDobMutation,
	useUpdateSurveyAnswersMutation,
	useSetPreferredSurveyLanguageMutation,
	useSendContactRequestMutation,
	useFetchSurveyLanguageQuery,
	useSendConsentConfirmationEmailMutation,
	useUpdateContactInfoMutation,
} = patientSurveyApi;
